'use client'

import React, { useMemo, useState } from 'react'
import { CheckCircle2, Lock, Mail, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCurrentUser } from '@/hooks/useCurrentUser'

type Step = 'password' | 'otp' | 'verifying_otp' | 'saving_password'

export function CompleteAccountGatedActionButton({
  children,
  onAuthedAction,
  redirectTo,
}: {
  children: React.ReactNode
  onAuthedAction: () => void | Promise<void>
  redirectTo?: string
}) {
  const { user, scope, refreshUser } = useCurrentUser() as any

  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<Step>('password')

  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const [err, setErr] = useState<string | null>(null)

  const needsCompletion = useMemo(() => {
    return scope !== 'authenticated'
  }, [scope])

  const currentEmail = String(user?.email || email || '').trim().toLowerCase()

  async function runFinalAction() {
    await refreshUser()
    await onAuthedAction()

    if (redirectTo) {
      window.location.href = redirectTo
      return
    }

    setOpen(false)
    setStep('password')
    setPassword('')
    setConfirm('')
    setCode('')
    setErr(null)
  }

  async function startOtp() {
    setErr(null)

    if (!currentEmail) {
      setErr('Necesitamos tu email para enviarte el código.')
      return
    }

    const res = await fetch('/api/auth/otp/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: currentEmail }),
    })

    const data = await res.json().catch(() => null)

    if (!res.ok || data?.ok === false) {
      setErr(data?.error || 'No se pudo enviar el código.')
      return
    }

    setStep('otp')
  }

  async function verifyOtp() {
    setErr(null)

    if (code.length !== 6) {
      setErr('Escribe el código de 6 dígitos.')
      return
    }

    setStep('verifying_otp')

    const res = await fetch('/api/auth/otp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })

    const data = await res.json().catch(() => null)

    if (!res.ok || data?.ok !== true) {
      setStep('otp')
      setErr(data?.error || 'Código inválido o expirado.')
      return
    }

    setCode('')
    setErr(null)
    setStep('password')
  }

  async function completeAccount() {
    setErr(null)

    if (password.length < 8) {
      setErr('Tu contraseña debe tener al menos 8 caracteres.')
      return
    }

    if (password !== confirm) {
      setErr('Las contraseñas no coinciden.')
      return
    }

    setStep('saving_password')

    const res = await fetch('/api/auth/complete-account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    })

    const data = await res.json().catch(() => null)

    if (data?.error === 'REAUTH_REQUIRED') {
      setPassword('')
      setConfirm('')
      setErr(null)
      await startOtp()
      return
    }

    if (!res.ok || data?.ok !== true) {
      setStep('password')
      setErr(data?.error || 'No se pudo activar tu cuenta.')
      return
    }

    await runFinalAction()
  }

  async function handleClick() {
    if (!needsCompletion) {
      await onAuthedAction()

      if (redirectTo) {
        window.location.href = redirectTo
      }

      return
    }

    setOpen(true)
    setStep('password')
    setErr(null)
  }

  function closeModal() {
    if (step === 'saving_password' || step === 'verifying_otp') return

    setOpen(false)
    setErr(null)
    setPassword('')
    setConfirm('')
    setCode('')
    setStep('password')
  }

  const isLoading = step === 'saving_password' || step === 'verifying_otp'

  return (
    <div className="space-y-3">
      <Button onClick={handleClick}>
        {children}
      </Button>

      {open && needsCompletion && (
        <div className="rounded-2xl border bg-background p-5 shadow-lg space-y-5">
          {step !== 'otp' && step !== 'verifying_otp' && (
            <>
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-primary/10 p-2 text-primary">
                  <Lock className="h-5 w-5" />
                </div>

                <div className="space-y-1">
                  <div className="text-sm font-semibold">
                    Guarda tu acceso
                  </div>

                  <div className="text-sm text-muted-foreground">
                    Crea una contraseña para entrar después sin códigos y continuar usando tu cuenta de forma segura.
                  </div>

                  {user?.email && (
                    <div className="text-xs text-muted-foreground pt-1">
                      Cuenta: <span className="font-medium">{user.email}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Crea tu contraseña"
                  type="password"
                  autoComplete="new-password"
                  disabled={isLoading}
                />

                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirma tu contraseña"
                  type="password"
                  autoComplete="new-password"
                  disabled={isLoading}
                />
              </div>

              <div className="flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  Por seguridad, si tu acceso temporal ya expiró, te pediremos confirmar tu email antes de guardar la contraseña.
                </div>
              </div>
            </>
          )}

          {(step === 'otp' || step === 'verifying_otp') && (
            <>
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-primary/10 p-2 text-primary">
                  <Mail className="h-5 w-5" />
                </div>

                <div className="space-y-1">
                  <div className="text-sm font-semibold">
                    Confirma tu email
                  </div>

                  <div className="text-sm text-muted-foreground">
                    Para proteger tu cuenta, enviamos un código de verificación antes de crear tu contraseña.
                  </div>
                </div>
              </div>

              {!user?.email && (
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  type="email"
                  autoComplete="email"
                  disabled={isLoading}
                />
              )}

              <Input
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                placeholder="Código de 6 dígitos"
                inputMode="numeric"
                autoComplete="one-time-code"
                disabled={isLoading}
              />
            </>
          )}

          {err && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {err}
            </div>
          )}

          {step !== 'otp' && step !== 'verifying_otp' && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={completeAccount} disabled={isLoading}>
                {step === 'saving_password' ? 'Activando…' : 'Activar cuenta'}
              </Button>

              <Button variant="ghost" onClick={closeModal} disabled={isLoading}>
                Después
              </Button>
            </div>
          )}

          {(step === 'otp' || step === 'verifying_otp') && (
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={verifyOtp}
                disabled={isLoading || code.length !== 6}
              >
                {step === 'verifying_otp' ? 'Verificando…' : 'Verificar código'}
              </Button>

              <Button
                variant="outline"
                onClick={startOtp}
                disabled={isLoading}
              >
                Reenviar código
              </Button>

              <Button variant="ghost" onClick={closeModal} disabled={isLoading}>
                Cancelar
              </Button>
            </div>
          )}

          {step === 'password' && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" />
              Sin códigos si completas tu cuenta ahora.
            </div>
          )}
        </div>
      )}
    </div>
  )
}